'use client';

import { useState } from 'react';
import {
  ApiLinkModel,
  AppSettingsPropertiesModel,
  ComponentSettingsPropertiesModel,
  CreateAccountFieldPropertiesModel,
  RichTextModel,
  SplashCreateAccountStepElementContentModel,
  SplashCreateAccountStepPropertiesModel,
} from '@/services/platform/api/model';
import { register } from '@/services/platform/data/authentication';
import HTMLParser from 'html-react-parser';
import ModuleWrapper from '../../../module/wrapper';
import {
  cn,
  ImagesToImageMap,
  ModuleProperties,
  SplashContext,
  useClientTranslation,
  useIosKeyboardHeight,
  useLocaleStore,
} from '@/lib/core';
import { LinksToLinkMap } from '@/lib/core/utils/links';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { Phone } from '@/components/ui/phone';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';

export const SplashCreateAccountStep = (props: ModuleProperties) => {
  const content = props.content as SplashCreateAccountStepElementContentModel | undefined;
  const appSettings = props.context?.settings?.properties as AppSettingsPropertiesModel | undefined;
  const translation = useClientTranslation();

  const { locale } = useLocaleStore();

  const appLogo = ImagesToImageMap(appSettings?.logo);

  const [form, setForm] = useState<Record<string, string>>({ countryCode: '+351' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [succeeded, setSucceeded] = useState(false);

  const [isPending, setIsPending] = useState(false);

  const keyboardHeight = useIosKeyboardHeight();

  if (!content) return null;

  const properties = content.properties as SplashCreateAccountStepPropertiesModel;
  const fields = (properties.fields?.items?.map(f => {
    const settings = f.settings?.properties as ComponentSettingsPropertiesModel | undefined;
    if (settings?.hide) return null;
    return f.content?.properties as CreateAccountFieldPropertiesModel | undefined;
  }) ?? []) as CreateAccountFieldPropertiesModel[];

  const visibleFields = fields.filter(f => !!f?.fieldSystemName);

  const createAccountLink = LinksToLinkMap(
    (properties?.createAccountLink ?? undefined) as unknown as ApiLinkModel[] | undefined
  );

  const successLoginLink = LinksToLinkMap(
    (properties?.successLoginLink ?? undefined) as unknown as ApiLinkModel[] | undefined
  );

  const maskDate = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  const maskPhone = (raw: string): string => raw.replace(/\D/g, '').slice(0, 9);

  const validateField = (field: CreateAccountFieldPropertiesModel, value: string): string => {
    const type = field.fieldType?.toLowerCase();
    if (field.required && !value.trim()) return field.errorMessage ?? translation('App.Labels.Required');
    if (type === 'email' && value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
      return field.errorMessage ?? translation('App.Labels.InvalidEmail');
    if (type === 'date' && value.trim()) {
      const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!match) return field.errorMessage ?? translation('App.Labels.InvalidDate');
      const d = new Date(+match[3], +match[2] - 1, +match[1]);
      if (d.getFullYear() !== +match[3] || d.getMonth() !== +match[2] - 1 || d.getDate() !== +match[1])
        return field.errorMessage ?? translation('App.Labels.InvalidDate');
    }
    if (type === 'mobile' && value.trim()) {
      if (!/^\d+$/.test(value)) return field.errorMessage ?? translation('App.Labels.InvalidPhoneOnlyDigits');
      if (value.length !== 9) return field.errorMessage ?? translation('App.Labels.InvalidPhone9Digits');
    }
    return '';
  };

  const hasMobileField = visibleFields.some(f => f.fieldType?.toLowerCase() === 'mobile');

  const handleChange = (key: string, value: string) => {
    if (key === 'countryCode') {
      setForm(prev => ({ ...prev, countryCode: value }));
      setErrors(prev => ({ ...prev, countryCode: value.trim() ? '' : translation('App.Labels.Required') }));
      return;
    }
    const field = visibleFields.find(f => f.fieldSystemName === key);
    const type = field?.fieldType?.toLowerCase();
    const next = type === 'date' ? maskDate(value) : type === 'mobile' ? maskPhone(value) : value;
    setForm(prev => ({ ...prev, [key]: next }));
    if (field) setErrors(prev => ({ ...prev, [key]: validateField(field, next) }));
  };

  const isFormValid =
    visibleFields.every(f => !validateField(f, form[f.fieldSystemName!] ?? '')) &&
    (!hasMobileField || !!form['countryCode']?.trim());

  const validate = () => {
    const next: Record<string, string> = {};
    visibleFields.forEach(f => {
      const error = validateField(f, form[f.fieldSystemName!] ?? '');
      if (error) next[f.fieldSystemName!] = error;
    });
    if (hasMobileField && !form['countryCode']?.trim())
      next['countryCode'] = translation('App.Labels.Required');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsPending(true);
    try {
      const mobileField = visibleFields.find(f => f.fieldType?.toLowerCase() === 'mobile');
      const phoneValue = mobileField ? (form[mobileField.fieldSystemName!] ?? '') : '';
      const result = await register({
        firstName: form['firstName'] ?? '',
        lastName: form['lastName'] ?? '',
        email: form['email'] ?? '',
        birthDate: (v => {
          const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
          return m ? `${m[3]}-${m[2]}-${m[1]}` : v;
        })(form['birthDate'] ?? ''),
        phoneNumber: phoneValue ? `${form['countryCode']}${phoneValue}` : null,
        origin: 'app',
        language: locale,
      });
      if (result?.success) {
        setSucceeded(true);
      } else {
        setErrors(prev => ({
          ...prev,
          form: result?.message ?? translation('App.Labels.RegistrationFailed'),
        }));
      }
    } finally {
      setIsPending(false);
    }
  };

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['step__create-account', 'justify-start!', 'min-h-[calc(100vh)] py-0 px-4 bg-white']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex', 'flex-col', 'flex-1', 'justify-between', 'gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <div className="pt-safe-area relative flex w-full flex-col gap-10 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
        <div className="relative flex size-11 w-full items-center justify-center">
          <button
            type="button"
            className="absolute left-0"
            aria-label="Go back"
            onClick={() => (props.context as SplashContext)?.onCreateAccount?.(false)}
          >
            <Icon
              name="long-arrow-up-left"
              width={24}
              height={24}
              strokeWidth={2}
              className="text-jncquoi-neutral-1000 h-6 w-6"
            />
          </button>
          {appLogo?.src && (
            <img
              src={appLogo.src}
              alt={appLogo.alt ?? translation('App.Labels.AppLogo')}
              className="h-6 w-auto object-contain"
            />
          )}
        </div>
        {succeeded ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
            <div className="flex flex-col items-center gap-2 text-center">
              {properties?.successTitle && (
                <Heading
                  as="h1"
                  className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[28px] leading-none font-semibold tracking-[0.5px]"
                >
                  {properties.successTitle}
                </Heading>
              )}
              {properties?.successDescription && (
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[16px] leading-none tracking-[0.25px]">
                  {properties.successDescription}
                </p>
              )}
            </div>
            <Button
              variant="primary-solid"
              palette="dark"
              size="md"
              className="w-full"
              onClick={() => (props.context as SplashContext)?.onLogin?.()}
            >
              {successLoginLink?.label ?? translation('App.Labels.Continue')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-1 flex-col gap-12">
            {/* Title */}
            <div className="flex flex-col items-center gap-2 text-center">
              {properties?.title && (
                <Heading
                  as="h1"
                  className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[28px] leading-none font-semibold tracking-[0.5px]"
                >
                  {properties.title}
                </Heading>
              )}
              {properties?.description && (
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[16px] leading-[100%] tracking-[0.25px]">
                  {properties.description}
                </p>
              )}
            </div>

            {/* Form + Buttons */}
            <div className="flex flex-1 flex-col justify-between gap-10">
              {/* Form */}
              <div className="flex flex-col gap-4">
                {visibleFields.map(field => {
                  const key = field.fieldSystemName!;
                  const type = field.fieldType?.toLowerCase() ?? 'text';

                  if (type === 'mobile') {
                    return (
                      <FormField
                        key={key}
                        id={key}
                        label={field.fieldName}
                        error={errors['countryCode'] || errors[key]}
                        required={!!field.required}
                      >
                        <Phone
                          id={key}
                          appSettings={appSettings}
                          countryCode={form['countryCode']}
                          value={form[key] ?? ''}
                          placeholder={field.fieldPlaceholder ?? undefined}
                          countryCodeError={!!errors['countryCode']}
                          error={!!errors[key]}
                          onCountryCodeChange={v => handleChange('countryCode', v)}
                          onChange={v => handleChange(key, v)}
                        />
                      </FormField>
                    );
                  }

                  return (
                    <FormField
                      key={key}
                      id={key}
                      label={field.fieldName}
                      error={errors[key]}
                      required={!!field.required}
                    >
                      <Input
                        id={key}
                        type={type === 'email' ? 'email' : 'text'}
                        placeholder={field.fieldPlaceholder ?? ''}
                        value={form[key] ?? ''}
                        error={!!errors[key]}
                        onChange={e => handleChange(key, e.target.value)}
                      />
                    </FormField>
                  );
                })}
              </div>

              {/* Bottom actions */}
              <div className="flex flex-col items-center gap-2">
                {errors.form && (
                  <p
                    role="alert"
                    className="font-pp-neue-montreal text-jncquoi-asia-7427c-500 w-full text-center text-[12px] tracking-[0.5px]"
                  >
                    {errors.form}
                  </p>
                )}
                {properties?.alreadyHaveAccount && (
                  <Button
                    variant="primary-solid"
                    palette="white"
                    size="lg"
                    className="w-full normal-case"
                    asChild
                    onClick={() => (props.context as SplashContext)?.onLogin()}
                  >
                    <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-center text-[14px] leading-[110%] font-normal tracking-[0.5px] [&_a]:pointer-events-none [&_a]:font-semibold! [&_a]:underline">
                      {HTMLParser((properties.alreadyHaveAccount as unknown as RichTextModel)?.markup ?? '')}
                    </div>
                  </Button>
                )}
                <Button
                  variant="primary-solid"
                  palette="dark"
                  size="lg"
                  className="w-full"
                  disabled={!isFormValid || isPending}
                  aria-busy={isPending}
                  onClick={handleSubmit}
                >
                  {createAccountLink?.label ?? translation('App.Labels.CreateAccount')}
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Reserves space above the iOS keyboard (Keyboard.resize: 'none' means the WKWebView
            never shrinks on its own), so the bottom fields can scroll into view instead of
            staying hidden behind it. */}
        <div
          aria-hidden
          className="shrink-0 transition-[height] duration-300 ease-out"
          style={{ height: keyboardHeight }}
        />
      </div>
    </ModuleWrapper>
  );
};
